'use client';

import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UnverifiedAssetProbeStatusResponse } from '@terra/shared-contracts-assets';

export const UNVERIFIED_ASSET_VERIFIED_ENDPOINT_WAIT_MS = 120_000;

export type UnverifiedAssetProbeWatchDisplay = {
  method: string;
  endpoint: string;
};

type UseUnverifiedAssetProbeWatchArgs = {
  probeStatusQueryKey: readonly unknown[];
  fetchProbeStatus: () => Promise<UnverifiedAssetProbeStatusResponse>;
  enabled: boolean;
  method: string;
  endpoint: string;
  onProbeSettled: () => void | Promise<unknown>;
  verifiedEndpointWaitMs?: number;
};

export function useUnverifiedAssetProbeWatch({
  probeStatusQueryKey,
  fetchProbeStatus,
  enabled,
  method,
  endpoint,
  onProbeSettled,
  verifiedEndpointWaitMs = UNVERIFIED_ASSET_VERIFIED_ENDPOINT_WAIT_MS,
}: UseUnverifiedAssetProbeWatchArgs) {
  const [isWatchingProbe, setIsWatchingProbe] = useState(false);
  const [watchStartedAt, setWatchStartedAt] = useState<number | null>(null);
  const [verifiedWithoutEndpointSince, setVerifiedWithoutEndpointSince] =
    useState<number | null>(null);
  const [verifiedEndpointId, setVerifiedEndpointId] = useState<string | null>(
    null,
  );
  const [verifiedAssetDisplay, setVerifiedAssetDisplay] =
    useState<UnverifiedAssetProbeWatchDisplay | null>(null);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

  const { data: probeStatus, dataUpdatedAt } = useQuery({
    queryKey: probeStatusQueryKey,
    queryFn: fetchProbeStatus,
    enabled: enabled && isWatchingProbe,
    refetchInterval: isWatchingProbe ? 2500 : false,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (!isWatchingProbe || !probeStatus || watchStartedAt === null) {
      return;
    }

    if (dataUpdatedAt < watchStartedAt) {
      return;
    }

    if (probeStatus.status === 'PENDING') {
      setVerifiedWithoutEndpointSince(null);

      return;
    }

    if (probeStatus.status === 'VERIFIED' && !probeStatus.endpointId) {
      const waitStartedAt = verifiedWithoutEndpointSince ?? Date.now();

      if (verifiedWithoutEndpointSince === null) {
        setVerifiedWithoutEndpointSince(waitStartedAt);
      }

      if (Date.now() - waitStartedAt < verifiedEndpointWaitMs) {
        return;
      }

      setIsWatchingProbe(false);
      setWatchStartedAt(null);
      setVerifiedWithoutEndpointSince(null);
      void Promise.resolve(onProbeSettled());

      return;
    }

    setIsWatchingProbe(false);
    setWatchStartedAt(null);
    setVerifiedWithoutEndpointSince(null);

    void Promise.resolve(onProbeSettled());

    if (probeStatus.status === 'VERIFIED' && probeStatus.endpointId) {
      setVerifiedEndpointId(probeStatus.endpointId);
      setVerifiedAssetDisplay({ method, endpoint });
      setIsSuccessDialogOpen(true);
    }
  }, [
    dataUpdatedAt,
    endpoint,
    isWatchingProbe,
    method,
    onProbeSettled,
    probeStatus,
    verifiedEndpointWaitMs,
    verifiedWithoutEndpointSince,
    watchStartedAt,
  ]);

  const beginWatching = useCallback(() => {
    setVerifiedEndpointId(null);
    setVerifiedWithoutEndpointSince(null);
    setWatchStartedAt(Date.now());
    setIsWatchingProbe(true);
  }, []);

  return {
    isWatchingProbe,
    isSuccessDialogOpen,
    setIsSuccessDialogOpen,
    verifiedEndpointId,
    verifiedAssetDisplay,
    beginWatching,
  };
}
